import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Breadcrumb, Button, Form, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import AuthService from "../../services/auth.service";
import ElasticSearchService from "../../services/elasticSearch.service";
import ACService from "../../helpers/accessValidation.js";
import * as Constants from "../../components/constants";
import { POSITION_LABELS, POSITION_ORDER } from "../../helpers/inscriptionUtils";

const POSITION_OPTIONS = POSITION_ORDER;

function suggestedRangesFromRow(row) {
	const ranges = [];
	(row.blocks || []).forEach(block => {
		(block.suggestedRanges || []).forEach(range => {
			ranges.push({
				blockId: range.blockId || block.blockId,
				blockIndex: range.blockIndex != null ? range.blockIndex : block.blockIndex,
				fromLine: range.fromLine,
				toLine: range.toLine,
				position: range.position || "",
				personId: range.personId || ""
			});
		});
	});
	return ranges;
}

function isValidRange(range, row) {
	if (!range) {
		return false;
	}
	const fromLine = parseInt(range.fromLine, 10);
	const toLine = parseInt(range.toLine, 10);
	if (!Number.isFinite(fromLine) || !Number.isFinite(toLine) || fromLine < 1 || toLine < fromLine) {
		return false;
	}
	if (!POSITION_OPTIONS.includes(range.position)) {
		return false;
	}
	const block = (row.blocks || []).find(item =>
		(range.blockId && item.blockId === range.blockId) || item.blockIndex === range.blockIndex
	);
	if (block && toLine > (block.lineCount || (block.lines || []).length)) {
		return false;
	}
	return true;
}

export default function InscriptionMigration() {
	const navigate = useNavigate();
	const [currentUser, setCurrentUser] = useState(null);
	const [errors, setErrors] = useState(null);
	const [message, setMessage] = useState(null);
	const [successful, setSuccessful] = useState(false);
	const [isMigrating, setIsMigrating] = useState(false);
	const [isPreviewing, setIsPreviewing] = useState(false);
	const [isApplying, setIsApplying] = useState(false);
	const [cementries, setCementries] = useState([]);
	const [selectedCementry, setSelectedCementry] = useState(null);
	const [candidates, setCandidates] = useState([]);
	const [selectedIds, setSelectedIds] = useState({});
	const [rangesByTombstone, setRangesByTombstone] = useState({});
	const [expandedId, setExpandedId] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	useEffect(() => {
		const user = AuthService.getCurrentUser();
		if (!user) {
			navigate("/login");
			return;
		}
		if (!ACService.accessValidation(user, Constants.AC_ADMIN_NAVIGATION)) {
			setErrors("Sie haben keine Berechtigung, diese Seite aufzurufen.");
			return;
		}
		setCurrentUser(user);
		ElasticSearchService.loadCementriesForAutoComplete(-1).then(
			response => {
				setCementries(Array.isArray(response.data) ? response.data : []);
			},
			error => {
				setErrors(
					(error.response && error.response.data && error.response.data.message) ||
					error.message ||
					error.toString()
				);
			}
		);
	}, [navigate]);

	function apiMessage(error) {
		return (error.response && error.response.data && error.response.data.message) ||
			error.message ||
			error.toString();
	}

	function handleMigrateInschriften() {
		setIsMigrating(true);
		setErrors(null);
		setMessage(null);
		ElasticSearchService.migrateInschriften().then(
			response => {
				setSuccessful(true);
				setMessage(response.data && response.data.message ? response.data.message : "Migration abgeschlossen.");
				setIsMigrating(false);
			},
			error => {
				setSuccessful(false);
				setMessage(apiMessage(error));
				setIsMigrating(false);
			}
		);
	}

	function handlePreview() {
		setIsPreviewing(true);
		setErrors(null);
		setMessage(null);
		const cementryId = selectedCementry && selectedCementry.id ? selectedCementry.id : "";
		ElasticSearchService.previewDoppelstein(cementryId).then(
			response => {
				const rows = Array.isArray(response.data) ? response.data : [];
				setCandidates(rows);
				const nextSelected = {};
				const nextRanges = {};
				rows.forEach(row => {
					const suggested = suggestedRangesFromRow(row);
					nextRanges[row.tombstoneId] = suggested;
					nextSelected[row.tombstoneId] = suggested.length > 0;
				});
				setRangesByTombstone(nextRanges);
				setSelectedIds(nextSelected);
				setExpandedId(rows.length === 1 ? rows[0].tombstoneId : null);
				setSuccessful(true);
				setMessage(rows.length === 0
					? "Keine Doppelstein-Kandidaten gefunden."
					: rows.length + " Kandidat(en) gefunden.");
				setIsPreviewing(false);
			},
			error => {
				setSuccessful(false);
				setMessage(apiMessage(error));
				setIsPreviewing(false);
			}
		);
	}

	function toggleSelected(tombstoneId) {
		setSelectedIds(prev => ({
			...prev,
			[tombstoneId]: !prev[tombstoneId]
		}));
	}

	function removeFromPreview(tombstoneId) {
		setCandidates(prev => prev.filter(row => row.tombstoneId !== tombstoneId));
		setSelectedIds(prev => {
			const next = { ...prev };
			delete next[tombstoneId];
			return next;
		});
		setRangesByTombstone(prev => {
			const next = { ...prev };
			delete next[tombstoneId];
			return next;
		});
		setExpandedId(prev => prev === tombstoneId ? null : prev);
	}

	function rangesFor(row) {
		return rangesByTombstone[row.tombstoneId] || [];
	}

	function setRangesFor(tombstoneId, ranges) {
		setRangesByTombstone(prev => ({
			...prev,
			[tombstoneId]: ranges
		}));
	}

	function selectedApplyEntries() {
		return candidates
			.filter(row => selectedIds[row.tombstoneId])
			.map(row => ({
				tombstoneId: row.tombstoneId,
				ranges: rangesFor(row).filter(range => isValidRange(range, row))
			}))
			.filter(entry => entry.ranges.length > 0);
	}

	function addRange(row, block) {
		const lineCount = block.lineCount || (block.lines || []).length || 1;
		setRangesFor(row.tombstoneId, [
			...rangesFor(row),
			{
				blockId: block.blockId,
				blockIndex: block.blockIndex,
				fromLine: 1,
				toLine: lineCount,
				position: "",
				personId: ""
			}
		]);
		setSelectedIds(prev => ({
			...prev,
			[row.tombstoneId]: true
		}));
	}

	function updateRange(row, index, field, value) {
		setRangesFor(row.tombstoneId, rangesFor(row).map((range, rangeIndex) => (
			rangeIndex === index ? { ...range, [field]: value } : range
		)));
	}

	function removeRange(row, index) {
		setRangesFor(row.tombstoneId, rangesFor(row).filter((_, rangeIndex) => rangeIndex !== index));
	}

	function handleApply() {
		const entries = selectedApplyEntries();
		if (entries.length === 0) {
			return;
		}
		setIsApplying(true);
		setErrors(null);
		setMessage(null);
		ElasticSearchService.applyDoppelstein(entries).then(
			response => {
				setSuccessful(true);
				setMessage(response.data && response.data.message ? response.data.message : "Split angewendet.");
				setConfirmOpen(false);
				setIsApplying(false);
				handlePreview();
			},
			error => {
				setSuccessful(false);
				setMessage(apiMessage(error));
				setIsApplying(false);
			}
		);
	}

	if (!currentUser) {
		return (
			<Container fluid="xxl">
				<Row>
					<Col>
						<Breadcrumb className="breadcrumb-style">
							<Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
							<Breadcrumb.Item className="breadcrumb-item-style" active>Administration</Breadcrumb.Item>
							<Breadcrumb.Item className="breadcrumb-item-style" active>Inschriften-Migration</Breadcrumb.Item>
						</Breadcrumb>
					</Col>
				</Row>
				<Row>
					<Col>
						<h3>Inschriften-Migration</h3>
						{errors ? <div className="alert alert-danger" role="alert">{errors}</div> : <div>Seite wird geladen…</div>}
					</Col>
				</Row>
			</Container>
		);
	}

	const applyEntries = selectedApplyEntries();

	return (
		<Container fluid="xxl">
			<Row>
				<Col>
					<Breadcrumb className="breadcrumb-style">
						<Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
						<Breadcrumb.Item className="breadcrumb-item-style" href="/dashboard">Administration</Breadcrumb.Item>
						<Breadcrumb.Item className="breadcrumb-item-style" active>Inschriften-Migration</Breadcrumb.Item>
					</Breadcrumb>
				</Col>
			</Row>
			<Row>
				<Col>
					<h3>Inschriften-Migration</h3>
				</Col>
			</Row>

			<Row className="text-spacer">
				<Col>
					<h4>Bestand nach inschriften[]</h4>
					<p>
						Überträgt vorhandene Vorderseite, Postament und Rückseite in die neue Inschriften-Liste.
						Das bisherige Feld bleibt erhalten. Steine mit vorhandener Liste werden übersprungen.
						Markerzeilen „links“, „rechts“, „oben“, „unten“ und „mitte“ bleiben stehen.
					</p>
					<Button className="btn btn-primary" onClick={handleMigrateInschriften} disabled={isMigrating || isPreviewing || isApplying}>
						{isMigrating ? "Migration läuft…" : "Bestand überführen"}
					</Button>
				</Col>
			</Row>

			<Row className="top-spacer">
				<Col>
					<h4>Doppelstein-Split</h4>
					<p>
						Kandidaten sind Grabsteine mit mehr als einer Person oder mit ganzen Zeilen
						„links“, „rechts“, „oben“, „unten“ oder „mitte“.
						Der Split braucht Zeilenbereiche von–bis und die Zielposition. Markerzeilen werden als Vorschlag vorausgefüllt.
						Einzelne Vorschläge können aus der Liste entfernt werden; der Grabstein selbst bleibt unverändert.
					</p>
					<Row>
						<Col md={6}>
							<Autocomplete
								options={cementries}
								getOptionLabel={(option) => option && option.label ? option.label : ""}
								value={selectedCementry}
								onChange={(event, value) => setSelectedCementry(value)}
								renderInput={(params) => <TextField {...params} label="Friedhof (leer = alle)" />}
							/>
						</Col>
						<Col md={6}>
							<Button className="btn btn-primary me-2" onClick={handlePreview} disabled={isMigrating || isPreviewing || isApplying}>
								{isPreviewing ? "Vorschau wird geladen…" : "Vorschau laden"}
							</Button>
							<Button className="btn btn-secondary" onClick={() => setConfirmOpen(true)} disabled={isMigrating || isPreviewing || isApplying || applyEntries.length === 0}>
								Ausgewählte anwenden
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>

			{message && (
				<Row className="text-spacer">
					<Col>
						<div className={successful ? "alert alert-success" : "alert alert-danger"} role="alert">{message}</div>
					</Col>
				</Row>
			)}

			<Row className="top-spacer">
				<Col>
					<Table striped bordered hover>
						<thead>
							<tr>
								<th></th>
								<th>Grabstein</th>
								<th>Grund</th>
								<th>Vorher</th>
								<th>Vorschlag</th>
								<th style={{ minWidth: "12rem" }}>Aktionen</th>
							</tr>
						</thead>
						<tbody>
							{candidates.length === 0 && (
								<tr>
									<td colSpan="6">Keine Kandidaten in der Vorschau.</td>
								</tr>
							)}
							{candidates.map(row => {
								const ranges = rangesFor(row);
								const validCount = ranges.filter(range => isValidRange(range, row)).length;
								const expanded = expandedId === row.tombstoneId;
								return (
									<React.Fragment key={row.tombstoneId}>
										<tr>
											<td>
												<Form.Check
													type="checkbox"
													disabled={validCount === 0}
													checked={Boolean(selectedIds[row.tombstoneId]) && validCount > 0}
													onChange={() => toggleSelected(row.tombstoneId)}
												/>
											</td>
											<td>{row.tombstoneId}</td>
											<td>{(row.reasons || []).join("; ")}</td>
											<td>{row.beforeSummary}</td>
											<td>
												{validCount > 0
													? validCount + " Bereich(e)"
													: (row.afterHeadings || []).join(" | ")}
											</td>
											<td style={{ minWidth: "12rem" }}>
												<div className="d-flex flex-column align-items-start">
													<Button variant="link" className="p-0" onClick={() => setExpandedId(expanded ? null : row.tombstoneId)}>
														{expanded ? "Bereiche schließen" : "Bereiche bearbeiten"}
													</Button>
													<Button variant="link" className="p-0" onClick={() => removeFromPreview(row.tombstoneId)}>
														Entfernen
													</Button>
													<a href={"/management/tombstone/" + row.cementryId + "/" + row.tombstoneId}>Editor</a>
												</div>
											</td>
										</tr>
										{expanded && (
											<tr>
												<td colSpan="6">
													{(row.blocks || []).map(block => {
														const blockRanges = ranges
															.map((range, index) => ({ range, index }))
															.filter(item =>
																(item.range.blockId && item.range.blockId === block.blockId)
																|| item.range.blockIndex === block.blockIndex
															);
														return (
															<div key={block.blockId || block.blockIndex} className="mb-4">
																<strong>{block.heading}</strong>
																<Table size="sm" bordered className="mt-2 mb-2">
																	<thead>
																		<tr>
																			<th style={{ width: "4rem" }}>Zeile</th>
																			<th>Inschrift</th>
																			<th>Übersetzung</th>
																		</tr>
																	</thead>
																	<tbody>
																		{(block.lines || []).map(line => (
																			<tr key={line.number} className={line.marker ? "table-warning" : ""}>
																				<td>{line.number}{line.marker ? " *" : ""}</td>
																				<td dir="auto">{line.text}</td>
																				<td>{line.translation}</td>
																			</tr>
																		))}
																	</tbody>
																</Table>
																<Table size="sm" bordered>
																	<thead>
																		<tr>
																			<th>Von</th>
																			<th>Bis</th>
																			<th>Position</th>
																			<th>Person</th>
																			<th></th>
																		</tr>
																	</thead>
																	<tbody>
																		{blockRanges.length === 0 && (
																			<tr>
																				<td colSpan="5">Keine Bereiche. Zeilen von–bis und Zielposition hinzufügen.</td>
																			</tr>
																		)}
																		{blockRanges.map(({ range, index }) => (
																			<tr key={index}>
																				<td>
																					<Form.Control
																						type="number"
																						min="1"
																						max={block.lineCount || 1}
																						value={range.fromLine}
																						onChange={(e) => updateRange(row, index, "fromLine", Number(e.target.value))}
																						style={{ width: "5.5rem" }}
																					/>
																				</td>
																				<td>
																					<Form.Control
																						type="number"
																						min="1"
																						max={block.lineCount || 1}
																						value={range.toLine}
																						onChange={(e) => updateRange(row, index, "toLine", Number(e.target.value))}
																						style={{ width: "5.5rem" }}
																					/>
																				</td>
																				<td>
																					<Form.Control
																						as="select"
																						value={range.position || ""}
																						onChange={(e) => updateRange(row, index, "position", e.target.value)}
																					>
																						{POSITION_OPTIONS.map(position => (
																							<option key={position || "ganze-flaeche"} value={position}>{POSITION_LABELS[position] || position}</option>
																						))}
																					</Form.Control>
																				</td>
																				<td>
																					<Form.Control
																						as="select"
																						value={range.personId || ""}
																						onChange={(e) => updateRange(row, index, "personId", e.target.value)}
																					>
																						<option value="">keine Zuordnung</option>
																						{(row.persons || []).map(person => (
																							<option key={person.id} value={person.id}>{person.name || person.id}</option>
																						))}
																					</Form.Control>
																				</td>
																				<td>
																					<Button variant="link" onClick={() => removeRange(row, index)}>Entfernen</Button>
																				</td>
																			</tr>
																		))}
																	</tbody>
																</Table>
																<Button variant="link" className="p-0" onClick={() => addRange(row, block)}>
																	Bereich hinzufügen
																</Button>
															</div>
														);
													})}
												</td>
											</tr>
										)}
									</React.Fragment>
								);
							})}
						</tbody>
					</Table>
				</Col>
			</Row>

			<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
				<DialogTitle>Doppelstein-Split anwenden?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{applyEntries.length} Grabstein/Grabsteine werden nach den angegebenen Zeilenbereichen gesplittet. Die Änderung wird gespeichert.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmOpen(false)} disabled={isApplying}>Abbrechen</Button>
					<Button onClick={handleApply} disabled={isApplying}>{isApplying ? "Wird gespeichert…" : "Anwenden"}</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}
